/**
 * NodePanel.tsx — Painel lateral com conteúdo de uma nota do vault
 *
 * Renderiza o markdown da nota com suporte a wikilinks clicáveis.
 * Abre via /node/:id ou como painel lateral na Home.
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { doc, getDoc, collection, getDocs } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { stripFrontmatter } from '../lib/markdown'
import { CATEGORY_COLORS } from './Graph3D'

interface VaultNote {
  id: string
  title: string
  category: string
  path: string
  content_md: string
  frontmatter: Record<string, unknown>
  wikilinks_out: string[]
  updated_at: { toDate: () => Date }
}

interface NodePanelProps {
  noteId: string
  onClose?: () => void
}

/**
 * Gera slug de uma string (espelha a lógica do ingest e do Home.tsx).
 */
function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Cache local de slug → docId para evitar refetch. */
const slugToIdCache: Map<string, string> = new Map()
let slugCacheLoaded = false
let slugCachePromise: Promise<void> | null = null

async function ensureSlugCache() {
  if (slugCacheLoaded) return
  if (slugCachePromise) return slugCachePromise

  slugCachePromise = getDocs(collection(db, 'vault_notes')).then((snap) => {
    for (const d of snap.docs) {
      const data = d.data() as { title?: string; path?: string }
      const id = d.id
      // Mapeia slug(title)
      if (data.title) {
        const s = slugify(data.title)
        if (s && !slugToIdCache.has(s)) slugToIdCache.set(s, id)
      }
      // Mapeia slug(filename sem .md)
      if (data.path) {
        const fname = data.path.split('/').pop()?.replace(/\.md$/i, '')
        if (fname) {
          const s = slugify(fname)
          if (s && !slugToIdCache.has(s)) slugToIdCache.set(s, id)
        }
      }
      // id → id (caso o wikilink já seja um doc id)
      if (!slugToIdCache.has(id)) slugToIdCache.set(id, id)
    }
    slugCacheLoaded = true
  })

  return slugCachePromise
}

/** Resolve um slug/título de wikilink para o docId do Firestore. */
function resolveWikilink(raw: string): string | null {
  // Tenta direto
  if (slugToIdCache.has(raw)) return slugToIdCache.get(raw)!
  // Tenta slugificado
  const s = slugify(raw)
  return slugToIdCache.get(s) ?? null
}

export function NodePanel({ noteId, onClose }: NodePanelProps) {
  const navigate = useNavigate()
  const [note, setNote] = useState<VaultNote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Títulos resolvidos para os wikilinks_out do rodapé
  const [resolvedLinks, setResolvedLinks] = useState<{ slug: string; id: string; title: string }[]>([])

  // Carrega nota + slug cache em paralelo
  useEffect(() => {
    if (!noteId) return
    setLoading(true)
    setError(null)
    setResolvedLinks([])

    Promise.all([
      getDoc(doc(db, 'vault_notes', noteId)),
      ensureSlugCache(),
    ])
      .then(([snap]) => {
        if (!snap.exists()) {
          setError('Nota não encontrada.')
          return
        }
        const n = { id: snap.id, ...snap.data() } as VaultNote
        setNote(n)

        // Resolve links do rodapé com títulos reais
        const resolved = (n.wikilinks_out ?? []).map((slug) => {
          const id = resolveWikilink(slug)
          // tenta pegar title do cache (não temos aqui ainda — carregamos sob demanda)
          return { slug, id: id ?? '', title: '' }
        }).filter((l) => l.id)

        // Busca títulos dos links resolvidos (batch de getDoc)
        if (resolved.length > 0) {
          Promise.all(
            resolved.slice(0, 12).map((l) =>
              getDoc(doc(db, 'vault_notes', l.id)).then((s) => ({
                ...l,
                title: s.exists() ? (s.data().title as string) : l.slug,
              }))
            )
          ).then(setResolvedLinks)
        }
      })
      .catch((err) => setError(`Erro ao carregar nota: ${err.message}`))
      .finally(() => setLoading(false))
  }, [noteId])

  // Navega para um wikilink resolvendo slug → docId
  const handleWikilinkClick = useCallback((raw: string) => {
    const id = resolveWikilink(raw)
    if (id) navigate(`/node/${id}`)
    else console.warn('[NodePanel] wikilink não resolvido:', raw)
  }, [navigate])

  // Handler global para wikilinks injetados via rehype-raw
  useEffect(() => {
    window.__wikilinkClick = handleWikilinkClick
    return () => { delete window.__wikilinkClick }
  }, [handleWikilinkClick])

  const categoryColor =
    note
      ? Object.entries(CATEGORY_COLORS).find(([key]) =>
          note.category.replace(/[^a-zA-Z0-9-]/g, '').startsWith(key.replace(/[^a-zA-Z0-9-]/g, ''))
        )?.[1] ?? '#607D8B'
      : '#607D8B'

  if (loading) {
    return (
      <div style={panelStyle}>
        <div style={{ color: '#5a5a80', padding: '24px', fontSize: '13px' }}>Carregando...</div>
      </div>
    )
  }

  if (error || !note) {
    return (
      <div style={panelStyle}>
        <div style={{ color: '#E91E63', padding: '24px', fontSize: '13px' }}>
          {error ?? 'Nota não encontrada.'}
        </div>
      </div>
    )
  }

  const cleanContent = stripFrontmatter(note.content_md)

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div
        style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              marginBottom: '8px',
              background: `${categoryColor}22`,
              color: categoryColor,
              border: `1px solid ${categoryColor}44`,
            }}
          >
            {note.category}
          </div>
          <h1
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#e8e8f0',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {note.title}
          </h1>
          <div style={{ fontSize: '11px', color: '#5a5a80', marginTop: '6px' }}>{note.path}</div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={() => navigate(`/node/${noteId}`)}
            className="btn"
            style={{ padding: '4px 10px', fontSize: '11px' }}
            title="Abrir em página dedicada"
          >
            ↗
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="btn"
              style={{ padding: '4px 10px', fontSize: '11px' }}
              title="Fechar painel"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Conteúdo markdown */}
      <div
        style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}
        className="markdown-body"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            a: ({ href, children }) => {
              // Links internos: /node/<slug-ou-id>
              if (href?.startsWith('/node/')) {
                const raw = href.replace('/node/', '')
                return (
                  <span
                    className="wikilink"
                    onClick={() => handleWikilinkClick(raw)}
                    style={{ cursor: 'pointer' }}
                  >
                    {children}
                  </span>
                )
              }
              return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              )
            },
          }}
        >
          {/* Converte [[Wikilinks]] para markdown links antes de renderizar */}
          {cleanContent.replace(
            /\[\[([^\]|#]+)(?:#[^\]|]*)?\|?([^\]]*)\]\]/g,
            (_, target, alias) => {
              const display = (alias.trim() || target.trim()).replace(/`/g, '')
              return `[${display}](/node/${target.trim()})`
            }
          )}
        </ReactMarkdown>
      </div>

      {/* Footer — links resolvidos com títulos reais */}
      {resolvedLinks.length > 0 && (
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: '10px', color: '#5a5a80', width: '100%', marginBottom: '4px' }}>
            Links desta nota
          </span>
          {resolvedLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => navigate(`/node/${l.id}`)}
              style={{
                padding: '3px 8px',
                borderRadius: '4px',
                background: 'rgba(87, 245, 195, 0.08)',
                border: '1px solid rgba(87, 245, 195, 0.2)',
                color: '#57f5c3',
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              {l.title || l.slug}
            </button>
          ))}
          {note.wikilinks_out.length > 12 && (
            <span style={{ fontSize: '11px', color: '#5a5a80' }}>
              +{note.wikilinks_out.length - 12} mais
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const panelStyle: React.CSSProperties = {
  width: 'var(--panel-w, 420px)',
  maxWidth: '100vw',
  height: 'var(--panel-h, 100%)',
  background: 'rgba(13, 13, 30, 0.97)',
  borderLeft: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  flexDirection: 'column',
  backdropFilter: 'blur(20px)',
  overflowY: 'hidden',
}

declare global {
  interface Window {
    __wikilinkClick?: (id: string, display?: string) => void
  }
}
